import React, { useEffect, useState, useContext } from "react";
import { useSnackbar } from "notistack";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Collapse,
  Box, Tabs, Tab, Autocomplete
} from '@mui/material';
import AppLayout from "../../components/AppLayout/AppLayout";
import ButtonContained from "../../components/StyledComponents/ButtonContained";
import ButtonOutlined from "../../components/StyledComponents/ButtonOutlined";
import AttachmentIcon from '@mui/icons-material/Attachment';
import CancelIcon from '@mui/icons-material/Cancel';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PendingIcon from '@mui/icons-material/Pending';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import InfoIcon from '@mui/icons-material/Info';
import BlockIcon from '@mui/icons-material/Block';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';


import currencyData from '../../utils/currency-codes.json';

import httpRequest from "../../httpRequest";

import UserContext from "../../context/user-context";


 // Convert currency data into an array of options
 const currencyOptions = Object.keys(currencyData).map(code => ({
  code,
  ...currencyData[code],
}));

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
};

const MyExpenses = () => {
  const { enqueueSnackbar } = useSnackbar();
  const userCtx = useContext(UserContext); // Access user context
  const { employeeData } = userCtx; // Get employee data from context

  const [selectedTab, setSelectedTab] = useState(0);
  

  const [expenses, setExpenses] = useState({ pending: [], approved: [], rejected: [] });
  const [companies, setCompanies] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);

  const [expandedRows, setExpandedRows] = useState({});
  const [employeeCode, setEmployeeCode] = useState('');
  const [signatures, setSignatures] = useState([]);

  const [positionId, setPositionId] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [baseCurrency, setBaseCurrency] = useState('');
  const [conversionRates, setConversionRates] = useState({});


  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (baseCurrency) {
      fetchConversionRates();
    }
  }, [baseCurrency]);

  
 useEffect(() => {
  if (employeeData) {
    setCompanies(employeeData.companies);
    setFunctions(employeeData.functions);
    setPositionId(employeeData.position_id);
    setEmployeeCode(employeeData.employee_code);
    setSelectedCompany(employeeData.company_id);
    setBaseCurrency('USD'); // Reset base currency
    fetchExpenses(employeeData.employee_code);
  }
}, [employeeData]);

  // Fetch base currency when the selected company changes
  useEffect(() => {
    const fetchBaseCurrency = async () => {
      if (selectedCompany) {
        try {
          const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-companies?id=${selectedCompany}`);
          const { base_currency } = response.data.company;
          setBaseCurrency(base_currency);
        } catch (error) {
          console.error("Error fetching company data:", error);
          enqueueSnackbar("Error fetching company data", { variant: "error" });
        }
      }
    };

    fetchBaseCurrency();
  }, [selectedCompany]);

  const fetchCategories = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      enqueueSnackbar("Error fetching categories", { variant: "error" });
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchExpenses = async (employeeCode) => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-expenses?employee_code=${employeeCode}`);
      const expensesData = response.data.expenses;
  
      const pending = expensesData.filter(exp => exp.status === 'Pending');
      const initiated = expensesData.filter(exp => exp.status === 'Initiated');
      const approved = expensesData.filter(exp => exp.status === 'Approved');
      const cancelled = expensesData.filter(exp => exp.status === 'Cancelled');
  
      setExpenses({ pending, initiated, approved, cancelled });
    } catch (error) {
      console.error("Error fetching expenses:", error);
      enqueueSnackbar("Error fetching expenses", { variant: "error" });
    }
  };
  

  const fetchConversionRates = async () => {
    try {
      const response = await fetch(`https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${baseCurrency.toLowerCase()}.json`);
      const data = await response.json();
      setConversionRates(data[baseCurrency.toLowerCase()]);
    } catch (error) {
      console.error("Error fetching conversion rates:", error);
      enqueueSnackbar("Error fetching conversion rates", { variant: "error" });
    }
  };

  const fetchSignatures = async (userIds) => {
    console.log('User IDs:', userIds);
    try {
      // Construct the API endpoint URL with the comma-separated list of user IDs and the user_id parameter
      const usersResponse = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-users?user_id=${userIds.join(',')}&exclude_fields=avatar`);
      const users = usersResponse.data.users;
  
      // Map over the users to fetch their signatures and details
      const signaturesData = await Promise.all(users.map(async (user) => {
        const employeeDetailsData = user.employee;
        const employeeName = employeeDetailsData?.employee_name || 'N/A';
        const positionName = employeeDetailsData?.position_name || 'N/A';
        return {
          id: user.user_id,
          signature: user.signature,
          employee_name: employeeName,
          position_name: positionName,
        };
      }));

      console.log('Signatures data:', signaturesData);
  
      // Set signatures state with the fetched data
      setSignatures(prev => ({
        ...prev,
        [userIds.join(',')]: signaturesData
      }));
    } catch (error) {
      console.error("Error fetching signatures:", error);
      enqueueSnackbar("Error fetching signatures", { variant: "error" });
    }
  };
  
  
  
  
  
  

  const convertAmount = (amount, currency) => {
    const rate = conversionRates[currency.toLowerCase()];
    return amount / rate;
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleOpenFile = (fileData, fileType) => {
    const byteCharacters = atob(fileData); // Decode base64
    const byteNumbers = new Array(byteCharacters.length)
      .fill()
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: fileType });
  
    const blobUrl = URL.createObjectURL(blob);
    const windowFeatures = "width=600,height=800";
  
    window.open(blobUrl, "_blank", windowFeatures);
  };
  

  const handleEditClick = (expense) => {
    setSelectedExpense(expense);
    setEditDialogOpen(true);
  };

  const handleReasonClick = async (expense) => {
    setSelectedExpense(expense);
    setReasonDialogOpen(true);
  
    // Fetch the employee's data from the API
    const employeeResponse = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-employees?employee_code=${expense.rejected_by}`);
    const employeeData = employeeResponse.data.employees[0];
  
    // Fetch the employee's position from the API
    const positionResponse = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-positions?id=${employeeData.position_id}`);
    const positionData = positionResponse.data.position;
  
    // Update the selectedExpense state with the employee's name and position
    setSelectedExpense(prev => ({
      ...prev,
      rejected_by_name: employeeData.employee_name,
      rejected_by_position: positionData.position_name
    }));
  };
  

  const handleDelete = async (expenseId) => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/delete-child-expense/${expenseId}`);
      enqueueSnackbar("Expense cancelled successfully", { variant: "success" });
      fetchExpenses(employeeCode);
    } catch (error) {
      console.error("Error deleting expense:", error);
    }
  };

  const handleParentDelete = async (parentExpenseId) => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/delete-parent-expense/${parentExpenseId}`);
      enqueueSnackbar("Parent expense deleted successfully", { variant: "success" });
      fetchExpenses(employeeCode); // Refresh the expenses list
    } catch (error) {
      console.error("Error deleting parent expense:", error);
      enqueueSnackbar("Error deleting parent expense", { variant: "error" });
    }
  };
  

  const handleRelease = async (parentId) => {
     // Include user_id in the release request payload
     const payload = {
      user_id: userCtx.userId,
    };

    console.log(payload);

    try {
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/release-expense/${parentId}`, payload);
      enqueueSnackbar("Expenses released successfully", { variant: "success" });
      fetchExpenses(employeeCode);
    } catch (error) {
      console.error("Error releasing expenses:", error);
      enqueueSnackbar("Error releasing expenses", { variant: "error" });
    }
  };
  

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedExpense(null);
  };

  const handleReasonDialogClose = () => {
    setReasonDialogOpen(false);
    setSelectedExpense(null);
  };

  const handleSave = async () => {
    try {
      await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/update-expense/${selectedExpense.id}`, selectedExpense);
      enqueueSnackbar("Expense updated successfully", { variant: "success" });
      fetchExpenses(employeeCode);
      handleDialogClose();
    } catch (error) {
      console.error("Error updating expense:", error);
      enqueueSnackbar("Error updating expense", { variant: "error" });
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
  
    if (name === 'expense_date') {
      // Format the date string as "DD/MM/YYYY"
      const date = new Date(value);
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  
      setSelectedExpense(prev => ({
        ...prev,
        [name]: formattedDate,
      }));
    } else {
      setSelectedExpense(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleExpandClick = async (parentId, status) => {
    setExpandedRows(prev => ({
      ...prev,
      [parentId]: !prev[parentId]
    }));  
    // Find the parent expense object using the parentId parameter
    const filteredParents = expenses[status.toLowerCase()];
    const parentExpense = filteredParents.find(expense => expense.id === parentId);
    console.log('parentExpense:', parentExpense);
  
    // Check if the parent expense has an approval flow
    if (parentExpense && parentExpense.approval_flow.length > 0) {
      console.log('parentExpense has an approval flow:', parentExpense.approval_flow);
  
      // Call the fetchSignatures function with the approval flow array
      await fetchSignatures(parentExpense.approval_flow);
    } else {
      console.log('parentExpense does not have an approval flow');
    }
  };
  

  const renderTable = (status) => {
    if (!expenses[status.toLowerCase()]) {
      return null;
    }
    const filteredParents = expenses[status.toLowerCase()].filter(parent =>
      !parent.expenses.some(child => child.is_rejected)
    );
    const totalAmount = filteredParents.reduce((total, parentExpense) => {
      const convertedAmount = convertAmount(parentExpense.total_base_amount, parentExpense.base_currency);
      return total + convertedAmount;
    }, 0);
    
    return (
      <Paper elevation={3} sx={{ p: 2, borderRadius: 1, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.5)', marginTop: '16px', marginBottom: '16px' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#354a5f' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Expense Submit Date</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Month Year</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Company</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Function</TableCell>
                
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>Base Amount</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Base Currency</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {filteredParents.map(parentExpense => (
                <React.Fragment key={parentExpense.id}>
                  <TableRow sx={{ backgroundColor: '#354a5f', color: 'white' }}>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{formatDate(parentExpense.expense_submit_date)}</TableCell>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{parentExpense.month_year}</TableCell>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{companies.find(c => c.id === parentExpense.company_id)?.company_name || parentExpense.company_id}</TableCell>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{functions.find(f => f.id === parentExpense.function_id)?.function_name || parentExpense.function_id}</TableCell>
                    
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>{parentExpense.total_base_amount.toLocaleString()}</TableCell>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{parentExpense.base_currency}</TableCell>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{parentExpense.status}</TableCell>
                    <TableCell>
                    <IconButton
  sx={{ backgroundColor: '#354a5f', color: 'white', fontSize: '24px' }}
  onClick={() => handleExpandClick(parentExpense.id, status)}
>
  {expandedRows[parentExpense.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
</IconButton>
  {status === 'pending' && parentExpense.status === 'Pending' && (
    <>
      <Button
        onClick={() => handleRelease(parentExpense.id)}
        size="small"
        sx={{
          ml: 1,
          color: 'white', 
          borderColor: 'white', 
          '&:hover': {
            backgroundColor: 'white', 
            color: '#354a5f', 
            borderColor: 'white',
          }
        }}
        variant="outlined"
      >
        Release
      </Button>
      <IconButton
        onClick={() => handleParentDelete(parentExpense.id)}
        sx={{ ml: 1, backgroundColor: '#354a5f', color: 'white' }}
      >
        <DeleteIcon />
      </IconButton>
    </>
  )}
</TableCell>

                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Collapse in={expandedRows[parentExpense.id]}>
                        <Box sx={{ border: '2px solid #354a5f', borderRadius: '4px', overflow: 'hidden' }}>
                          <Table>
                            <TableHead sx={{ backgroundColor: '#354a5f' }}>
                              <TableRow>
                              <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Expense Date</TableCell>

                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Expense Name</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Description</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Comments</TableCell>
                                
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>Amount</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Currency</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>Base Amount</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Base Currency</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Category</TableCell>
                                
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Attachment</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}></TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}></TableCell>

                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {parentExpense.expenses.map(childExpense => (
                                <TableRow key={childExpense.id}>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{childExpense.expense_date}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{childExpense.expense_name}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{childExpense.expense_description}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{childExpense.comments}</TableCell>
                                  
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>{childExpense.amount.toLocaleString()}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{childExpense.currency}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>{childExpense.base_amount.toLocaleString()}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{childExpense.base_currency}</TableCell>
                                  
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{categories.find(cat => cat.id === childExpense.expense_category_id)?.expense_category || childExpense.expense_category_id}</TableCell>
                                 
                                  <TableCell>
                                  {childExpense.pdf_file && (
      <IconButton sx={{ backgroundColor: 'white', color: '#354a5f' }}
        onClick={() => handleOpenFile(childExpense.pdf_file, 'application/pdf')}
      >
       <AttachmentIcon />
      </IconButton>
    )}
    {childExpense.image_file && (
       <IconButton sx={{ backgroundColor: 'white', color: '#354a5f' }}
        onClick={() => handleOpenFile(childExpense.image_file, 'image/jpeg')}
      >
        <AttachmentIcon />
      </IconButton>
    )}
                                  </TableCell>
                                  {status === 'pending' && childExpense.status === 'Pending' && (
    <>
                                  <TableCell>
                                  
                                    <IconButton sx={{ backgroundColor: 'white', color: '#354a5f' }} onClick={() => handleEditClick(childExpense)} size="small">
                                      <EditIcon />
                                    </IconButton>
                                    </TableCell>
                                    <TableCell>
                                    <IconButton sx={{ ml: 1, backgroundColor: 'white', color: '#354a5f' }} onClick={() => handleDelete(childExpense.id)} size="small">
                                      <DeleteIcon />
                                    </IconButton>
                                   
                                  </TableCell>
                                  </>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          
                        </Box>
                        {signatures[parentExpense.approval_flow.join(',')] && signatures[parentExpense.approval_flow.join(',')].length > 0 && (
                  <Box sx={{ border: '2px solid #354a5f', borderRadius: '4px', overflow: 'hidden', marginTop: "20px" }}>
                    <Table>
                      <TableHead sx={{ backgroundColor: '#354a5f' }}>
                        <TableRow>
                          {signatures[parentExpense.approval_flow.join(',')]
                            .filter(signature => parentExpense.approval_flow.includes(signature.id))
                            .sort((a, b) => parentExpense.approval_flow.indexOf(a.id) - parentExpense.approval_flow.indexOf(b.id))
                            .map(signature => (
                              <TableCell key={signature.id} align="center" sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>
                                {signature.position_name}
                              </TableCell>
                            ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          {signatures[parentExpense.approval_flow.join(',')]
                            .filter(signature => parentExpense.approval_flow.includes(signature.id))
                            .sort((a, b) => parentExpense.approval_flow.indexOf(a.id) - parentExpense.approval_flow.indexOf(b.id))
                            .map(signature => (
                              <TableCell key={signature.id} align="center" sx={{ backgroundColor: 'white', fontFamily: "SapFiori72, sans-serif" }}>
                                <img src={signature.signature} alt={`Signature of ${signature.employee_name}`} style={{ height: '100px', width: '100px', objectFit: 'contain' }} />
                                <p>{signature.employee_name}</p>
                              </TableCell>
                            ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                )}



                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
            <TableFooter sx={{ backgroundColor: '#354a5f' }}>
              <TableRow>
                <TableCell colSpan={3} sx={{ color: 'white' }}></TableCell>
                <TableCell colSpan={1} sx={{ color: 'white', fontSize: '14px', fontFamily: "SapFiori72, sans-serif"  }}>Grand Total Amount</TableCell>
                <TableCell colSpan={1} sx={{ color: 'white', textAlign: "right", fontFamily: "SapFiori72, sans-serif", fontSize: '14px'  }}>{totalAmount.toLocaleString()}</TableCell>
                <TableCell colSpan={1} sx={{ color: 'white', textAlign: "left", fontFamily: "SapFiori72, sans-serif", fontSize: '14px'  }}>{baseCurrency}</TableCell>
                <TableCell colSpan={2} sx={{ color: 'white', textAlign: "right"  }}></TableCell>

              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    );
  };
  


  const renderRejectedExpensesTable = () => {
    const rejectedParents = expenses.pending.filter(parent => 
      parent.expenses.some(child => child.is_rejected)
    );
    const totalAmount = rejectedParents.reduce((total, rejectedParents) => {
      const convertedAmount = convertAmount(rejectedParents.total_base_amount, rejectedParents.base_currency);
      return total + convertedAmount;
    }, 0);
    return (
      <>
      <Paper elevation={3} sx={{ p: 2, borderRadius: 1, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.5)', marginTop: '16px' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#354a5f' }}>
              <TableRow>
              <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Expense Submit Date</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Month Year</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Company</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Function</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>Total Base Amount</TableCell>
                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Base Currency</TableCell>

                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}></TableCell>
                
              </TableRow>
            </TableHead>
            <TableBody>
              {rejectedParents.map(parentExpense => (
                <React.Fragment key={parentExpense.id}>
                  <TableRow sx={{ backgroundColor: '#354a5f', color: 'white' }}>
                    
                  <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{formatDate(parentExpense.expense_submit_date)}</TableCell>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{parentExpense.month_year}</TableCell>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{companies.find(c => c.id === parentExpense.company_id)?.company_name || parentExpense.company_id}</TableCell>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{functions.find(f => f.id === parentExpense.function_id)?.function_name || parentExpense.function_id}</TableCell>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>{parentExpense.total_base_amount.toLocaleString()}</TableCell>
                    <TableCell sx={{ backgroundColor: '#354a5f', color: 'white', fontFamily: "SapFiori72, sans-serif" }}>{parentExpense.base_currency}</TableCell>

                    <TableCell>
                    <IconButton
  sx={{ backgroundColor: '#354a5f', color: 'white', fontSize: '24px' }}
  onClick={() => handleExpandClick(parentExpense.id)}
>
  {expandedRows[parentExpense.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
</IconButton>

<IconButton
        onClick={() => handleReasonClick(parentExpense.expenses[0])}
        sx={{ ml: 1, backgroundColor: '#354a5f', color: 'white' }}
      >
        <InfoIcon />
        </IconButton>
      <Button
        onClick={() => handleRelease(parentExpense.id)}
        sx={{
          ml: 1,
          color: 'white', 
          borderColor: 'white', 
          '&:hover': {
            backgroundColor: 'white', 
            color: '#354a5f', 
            borderColor: 'white',
          }
        }}
        size="small"
        variant="outlined"
      >
        Resubmit
      </Button>
      <Button
  onClick={() => handleDelete(parentExpense.expenses[0].id)}
  size="small"
  sx={{
    ml: 1,
    color: 'white', 
    borderColor: 'white', 
    '&:hover': {
      backgroundColor: 'white', 
      color: '#354a5f', 
      borderColor: 'white',
    }
  }}
  variant="outlined"
>
  Cancel
</Button>


     
</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Collapse in={expandedRows[parentExpense.id]}>
                      <Box sx={{ border: '2px solid #354a5f', borderRadius: '4px', overflow: 'hidden', marginBottom: '16px' }}>
                          <Table>
                            <TableHead sx={{ backgroundColor: '#354a5f' }}>
                              <TableRow>
                                
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Reject Reason</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                
                                <TableCell sx={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif" }}>{parentExpense.expenses[0].reject_reason}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>

                      <Box sx={{ border: '2px solid #354a5f', borderRadius: '4px', overflow: 'hidden' }}>

                        <Table>
                          <TableHead sx={{ backgroundColor: '#354a5f' }}>
                              <TableRow>
                              <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Expense Date</TableCell>

                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Expense Name</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Description</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Comments</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>Amount</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Currency</TableCell>

                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>Base Amount</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Base Currency</TableCell>

                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Category</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Attachment</TableCell>
                                
                                <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}></TableCell>
                              </TableRow>
                            </TableHead>
                          <TableBody>
                            {parentExpense.expenses.filter(child => child.is_rejected).map(rejectedChild => (
                              <TableRow key={rejectedChild.id}>


<TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{rejectedChild.expense_date}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{rejectedChild.expense_name}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{rejectedChild.expense_description}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{rejectedChild.comments}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>{rejectedChild.amount.toLocaleString()}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{rejectedChild.currency}</TableCell>

                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", textAlign: 'right' }}>{rejectedChild.base_amount.toLocaleString()}</TableCell>
                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{rejectedChild.base_currency}</TableCell>

                                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{categories.find(cat => cat.id === rejectedChild.expense_category_id)?.expense_category || rejectedChild.expense_category_id}</TableCell>
                                  <TableCell>
                                  {rejectedChild.pdf_file && (
       <IconButton sx={{ backgroundColor: 'white', color: '#354a5f' }}
        onClick={() => handleOpenFile(rejectedChild.pdf_file, 'application/pdf')}
      >
       <AttachmentIcon />
      </IconButton>
    )}
    {rejectedChild.image_file && (
       <IconButton sx={{ backgroundColor: 'white', color: '#354a5f' }}
        onClick={() => handleOpenFile(rejectedChild.image_file, 'image/jpeg')}
      >
        <AttachmentIcon />
      </IconButton>
    )}
                                  </TableCell>
                                  
                                  <TableCell>
                                 
                                    <IconButton sx={{ backgroundColor: 'white', color: '#354a5f' }} onClick={() => handleEditClick(rejectedChild)} size="small">
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton sx={{ ml: 1, backgroundColor: 'white', color: '#354a5f' }} onClick={() => handleDelete(rejectedChild.id)} size="small">
                                      <CancelIcon />
                                    </IconButton>

                                    
                                  </TableCell>



                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
            <TableFooter sx={{ backgroundColor: '#354a5f' }}>
              <TableRow>
                <TableCell colSpan={3} sx={{ color: 'white' }}></TableCell>
                <TableCell colSpan={1} sx={{ color: 'white', fontSize: '14px', fontFamily: "SapFiori72, sans-serif"  }}>Grand Total Amount</TableCell>
                <TableCell colSpan={1} sx={{ color: 'white', textAlign: "right", fontFamily: "SapFiori72, sans-serif", fontSize: '14px'  }}>{totalAmount.toLocaleString()}</TableCell>
                <TableCell colSpan={1} sx={{ color: 'white', textAlign: "left", fontFamily: "SapFiori72, sans-serif", fontSize: '14px'  }}>{baseCurrency}</TableCell>
                <TableCell colSpan={2} sx={{ color: 'white', textAlign: "right"  }}></TableCell>

              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
      </>
    );
  };
  

  return (
    <AppLayout>
      <Grid container justifyContent="center">
        <Grid item xs={10.8}>

        <Tabs value={selectedTab} onChange={handleTabChange} centered >
            <Tab label="Pending" sx={{ textTransform: 'none', fontFamily: 'SapFiori72, sans-serif' }} />
            <Tab label="Initiated" sx={{ textTransform: 'none', fontFamily: 'SapFiori72, sans-serif' }} />
            <Tab label="Approved" sx={{ textTransform: 'none', fontFamily: 'SapFiori72, sans-serif' }} />
            <Tab label="Rejected" sx={{ textTransform: 'none', fontFamily: 'SapFiori72, sans-serif' }} />
            <Tab label="Cancelled" sx={{ textTransform: 'none', fontFamily: 'SapFiori72, sans-serif' }} />
          </Tabs>

          {selectedTab === 0 && (
            <>
          

          <Typography variant="h3" sx={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif", marginTop: '16px' }}>
            <PendingIcon sx={{ fontSize: '40px' }} /> Pending Expenses
          </Typography>
          {renderTable('pending')}

          </>
          )}

{selectedTab === 1 && (
            <>
          <Typography variant="h3" sx={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif", marginTop: '16px' }}>
            <ArrowCircleUpIcon sx={{ fontSize: '40px' }} /> Initiated Expenses
          </Typography>
          {renderTable('initiated')}
          </>
          )}

{selectedTab === 2 && (
            <>
          <Typography variant="h3" sx={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif", marginTop: '16px' }}>
            <CheckCircleIcon sx={{ fontSize: '40px' }} /> Approved Expenses
          </Typography>
          {renderTable('approved')}
          </>
          )}

{selectedTab === 3 && (
            <>
          <Typography variant="h3" sx={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif", marginTop: '16px' }}>
            <CancelIcon sx={{ fontSize: '40px' }} /> Rejected Expenses
          </Typography>
          {renderRejectedExpensesTable()}
          </>
          )}

{selectedTab === 4 && (
            <>
          <Typography variant="h3" sx={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif", marginTop: '16px' }}>
            <BlockIcon sx={{ fontSize: '40px' }} /> Cancelled Expenses
          </Typography>
          {renderTable('cancelled')}
          </>
          )}

          {/* Edit Dialog */}
          {selectedExpense && (
            <Dialog open={editDialogOpen} onClose={handleDialogClose}>
              <DialogTitle  sx={{ fontFamily: "SapFiori72, sans-serif" }}>Edit Expense</DialogTitle>
              <DialogContent>
              <TextField 
  name="expense_date"
  label="Expense Date"
  type="date"
  value={selectedExpense.expense_date && selectedExpense.expense_date !== '00/00/0000' 
    ? new Date(selectedExpense.expense_date.split('/').reverse().join('-')).toISOString().split('T')[0] 
    : ''
  }
  onChange={handleChange}
  fullWidth
  margin="dense"
/>
              <TextField
                  name="expense_name"
                  label="Expense Name"
                  value={selectedExpense.expense_name || ''}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                />
                <TextField
                  name="expense_description"
                  label="Description"
                  value={selectedExpense.expense_description || ''}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                />
<TextField type="number"
                  name="amount"
                  label="Expense Amount"
                  value={selectedExpense.amount || ''}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                />

                 <Autocomplete
              options={currencyOptions.map(option => option.code)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Expense Currency"
                  fullWidth 
                  sx={{ minWidth: 100 }}
                />
              )}
              value={selectedExpense.currency || ''}
              onChange={handleChange}
            />
<TextField type="number"
                  name="base_amount"
                  label="Base Amount"
                  value={selectedExpense.base_amount || ''}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                />
<TextField
  name="base_currency"
  label="Base Currency"
  value={selectedExpense.base_currency || ''}
  InputProps={{
    readOnly: true,
  }}
  fullWidth
  margin="dense"
/>


                 

                <TextField
                  name="comments"
                  label="Comments"
                  value={selectedExpense.comments || ''}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                />
                <TextField
                  name="expense_category_id"
                  label="Category"
                  select
                  SelectProps={{ native: true }}
                  value={selectedExpense.expense_category_id || ''}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                >
                  <option value="" />
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.expense_category}
                    </option>
                  ))}
                </TextField>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDialogClose} color="primary">Cancel</Button>
                <Button onClick={handleSave} color="primary">Save</Button>
              </DialogActions>
            </Dialog>
          )}


 {/* Reason Dialog */}
 {selectedExpense && (
            <Dialog open={reasonDialogOpen} onClose={handleReasonDialogClose} maxWidth="md">
            <DialogTitle sx={{ fontFamily: "SapFiori72, sans-serif" }}>Rejection Reason</DialogTitle>
            <DialogContent>
            <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Rejected By:</b> {selectedExpense.rejected_by_name}</p>
                    <p style={{ fontFamily: "SapFiori72, sans-serif" }}><b>Position:</b> {selectedExpense.rejected_by_position}</p>
                    
              <Typography sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold" }}>Reason:</Typography>
              <Typography sx={{ fontFamily: "SapFiori72, sans-serif" }}>{selectedExpense.reject_reason}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleReasonDialogClose} color="primary">Close</Button>
            </DialogActions>
          </Dialog>
          
          )}


        </Grid>
      </Grid>
    </AppLayout>
  );
};

export default MyExpenses;
